import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const PowerPointViewer = ({ arrayBuffer, fileName = 'presentation.pptx', documentId }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Create PDF default layout plugin instance
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  
  // Function to handle download of the original PowerPoint file
  const handleDownload = () => {
    if (!arrayBuffer) return;
    
    try {
      const blob = new Blob([arrayBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'presentation.pptx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PowerPoint file:", error);
      setError("An error occurred while downloading the file. Please try again.");
    }
  };

  // Check if a PDF version is available from the server
  useEffect(() => {
    if (!arrayBuffer || !documentId) return;
    
    setLoading(true);
    setError(null);
    
    // Request the PDF version of the PowerPoint file
    const token = localStorage.getItem("token");
    const pdfUrl = `${process.env.REACT_APP_API_URL}/documents/${documentId}?token=${token}&pdf=true`;
    
    // Test if the PDF is available
    fetch(pdfUrl, { method: 'HEAD' })
      .then(response => {
        // Check if the response is OK and the content type is PDF
        if (response.ok && response.headers.get('content-type') === 'application/pdf') {
          setPdfUrl(pdfUrl);
          setLoading(false);
        } else {
          // The PDF conversion is not available, show the fallback view
          setError("PDF conversion not available for this PowerPoint file");
          setLoading(false);
        }
      })
      .catch(err => {
        console.error("Error checking PDF availability:", err);
        setError("Could not check for PDF version of this PowerPoint file");
        setLoading(false);
      });
  }, [arrayBuffer, documentId]);
  
  // If still loading, show spinner
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If we have a PDF URL, show the PDF viewer
  if (pdfUrl) {
    return (
      <Box sx={{ width: '100%', my: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            onClick={handleDownload}
          >
            Download Original PowerPoint
          </Button>
        </Box>
        
        <Box sx={{ border: '1px solid #ccc', height: '600px', overflow: 'auto' }}>
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer fileUrl={pdfUrl} plugins={[defaultLayoutPluginInstance]} />
          </Worker>
        </Box>
      </Box>
    );
  }
  
  // Fallback PowerPoint information card with download option
  return (
    <Box sx={{ width: '100%', my: 2 }}>
      {error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            {error}
          </Typography>
          <Typography variant="body2" paragraph>
            PowerPoint files can be viewed by:
          </Typography>
          <ol>
            <li>Downloading and opening in Microsoft PowerPoint or a compatible application</li>
            <li>Converting to PDF format on the server (requires LibreOffice installation)</li>
          </ol>
        </Alert>
      )}
      
      <Card sx={{ maxWidth: '100%', boxShadow: 3 }}>
        <CardMedia
          sx={{ 
            height: 140, 
            backgroundColor: '#f5f5f5', 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <SlideshowIcon sx={{ fontSize: 80, color: '#d04423' }} />
        </CardMedia>
        
        <CardContent>
          <Typography gutterBottom variant="h5" component="div" sx={{ wordBreak: 'break-word' }}>
            {fileName}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            PowerPoint Presentation
          </Typography>
          
          <Typography variant="body2" paragraph>
            <strong>Recommended action:</strong> Download this file and open it with Microsoft PowerPoint or a compatible application for the best viewing experience.
          </Typography>
          
          <Paper elevation={2} sx={{ p: 2, bgcolor: '#f8f9ff', mb: 2, border: '1px solid #e0e7ff' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#0033a0' }}>
              Server-side PowerPoint Viewing
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              To enable PowerPoint viewing directly in the browser, LibreOffice must be installed on the server:
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Windows Installation:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 1 }}>
              1. Download and install LibreOffice from <a href="https://www.libreoffice.org/download" target="_blank" rel="noopener noreferrer">libreoffice.org</a>
              <br />
              2. Verify installation by running: <code>libreoffice --version</code> in Command Prompt
              <br />
              3. Ensure the path <code>C:\Program Files\LibreOffice\program</code> is in your system PATH
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Deployment on Render:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Add to your build commands: <code>apt-get update && apt-get install -y libreoffice</code>
            </Typography>
          </Paper>
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<DownloadIcon />} 
            onClick={handleDownload}
            color="primary"
            sx={{ minWidth: 250, fontSize: '1.1rem', py: 1.5 }}
          >
            Download PowerPoint File
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default PowerPointViewer;